import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useMemo, useContext } from 'react';
import styled from 'styled-components';
import { useQuery } from '@apollo/client';

// Components
import LoadingArea from '@molecules/LoadingArea';
import { SearchPropertiesMap, SearchState } from '@search';
import EventCalendar from '@molecules/EventCalendar';

// Functionality
import { ViewTypeContext } from '@context/viewTypeContext';
import { useScreenSmDown } from '@hooks/useResponsive';

const SearchItemStyled = styled.div`
    @media (min-width: 1500px) {
        &.two-per-row {
            flex: 0 0 50%;
            max-width: 50%;
        }

        &.three-per-row {
            flex: 0 0 33.33333%;
            max-width: 33.33333%;
        }
    }
`;

export const SearchHitsGlobalForType = ({
    query,
    typePlural,
    hits,
    index,
    ListItemComponent,
    CardComponent,
    transformFunc,
    onClick,
    isLoggedIn,
    added,
    removed,
    resultsPerRow = 3,
    onMessageSent,
    SearchStateComponent = SearchState,
}) => {
    const { loading, error, data } = useQuery(query, { skip: !isLoggedIn });
    const [viewType] = useContext(ViewTypeContext);

    const favoriteIds = useMemo(() => {
        if (!data || !typePlural || !data[typePlural]) {
            return null;
        }

        const filtered = data[typePlural].filter(e => !!e);

        return filtered.map(c => c.recordId);
    }, [data, typePlural]);

    const isScreenSmDown = useScreenSmDown();

    const getIsFavorite = hitObjectId => {
        if (loading) return;
        const id = parseInt(hitObjectId.split('-')[1], 10);
        return favoriteIds && favoriteIds.includes(id);
    };

    const getIsFavoriteProperty = recordId => {
        if (loading) return;
        return favoriteIds && favoriteIds.includes(recordId);
    };

    const getIsFavoriteSavedSearchableMember = memberId => {
        if (loading) return;
        return favoriteIds && favoriteIds.includes(memberId);
    };

    const handleClick = (hit, url) => {
        if (onClick) {
            onClick({ hit, url, index: typePlural });
        }
    };

    const renderListItem = hit => {
        let isFavorite;

        if (typePlural === 'savedProperties') {
            isFavorite = getIsFavoriteProperty(hit.recordId);
        } else if (index === 'saved_searchable_members' || index === 'saved_searchable_members_non_distinct_virtual') {
            isFavorite = getIsFavoriteSavedSearchableMember(hit.id);
        } else {
            isFavorite = getIsFavorite(hit.objectID);
        }

        return (
            <div key={hit.objectID}>
                <ListItemComponent
                    isLoggedIn={isLoggedIn} // Get rid of this and use useAuthorized inside the list item instead
                    {...transformFunc({ hit, index, isFavorite })}
                    favorited={added}
                    unfavorited={removed}
                    hit={hit}
                    onClick={args => handleClick(hit, args)}
                    onMessageSent={() => onMessageSent({ hit })}
                />
            </div>
        );
    };

    const renderCard = hit => {
        let isFavorite;

        if (typePlural === 'savedProperties') {
            isFavorite = getIsFavoriteProperty(hit.recordId);
        } else if (index === 'saved_searchable_members' || index === 'saved_searchable_members_non_distinct_virtual') {
            isFavorite = getIsFavoriteSavedSearchableMember(hit.id);
        } else {
            isFavorite = getIsFavorite(hit.objectID);
        }

        return (
            <SearchItemStyled
                key={hit.objectID}
                className={classNames('d-flex flex-column mb-4', {
                    'col-xl-12': resultsPerRow === 1,
                    'col-xl-6': resultsPerRow === 2,
                    'col-xl-4': resultsPerRow === 3,
                    'two-per-row': resultsPerRow === 2,
                    'three-per-row': resultsPerRow === 3,
                })}
            >
                <CardComponent
                    isLoggedIn={isLoggedIn} // Get rid of this and use useAuthorized inside the card instead
                    {...transformFunc({ hit, index, isFavorite })}
                    favorited={added}
                    unfavorited={removed}
                    hit={hit}
                    onClick={args => handleClick(hit, args)}
                    onMessageSent={onMessageSent}
                />
            </SearchItemStyled>
        );
    };

    if (hits.length === 0) {
        return <SearchStateComponent />;
    }

    // blocking until favorites are loaded, don't love that, but the other
    // option is to 2-way binding on the useFavoritesContact hook
    if (loading) {
        return <LoadingArea />;
    }

    if (!isScreenSmDown && viewType === 'calendar') {
        return (
            <div className="mb-3">
                <EventCalendar hits={hits} />
            </div>
        );
    }

    if (isScreenSmDown) {
        return <div style={{ borderBottom: '1px solid #eceff1' }}>{hits.map(hit => renderListItem(hit))}</div>;
    }

    if (!isScreenSmDown && viewType === 'list') {
        return <div style={{ borderBottom: '1px solid #eceff1' }}>{hits.map(hit => renderListItem(hit))}</div>;
    }

    if (!isScreenSmDown && viewType === 'card') {
        return <div className="row">{hits.map(hit => renderCard(hit))}</div>;
    }

    if (!isScreenSmDown && viewType === 'map') {
        return (
            <div className="my-3">
                <SearchPropertiesMap displayLegend onPropertyFavorite={added} onPropertyClick={onClick} />
            </div>
        );
    }
};

SearchHitsGlobalForType.propTypes = {
    hits: PropTypes.any,
    index: PropTypes.string,
    typePlural: PropTypes.string,
    query: PropTypes.object,
    ListItemComponent: PropTypes.func,
    CardtemComponent: PropTypes.func,
    transformFunc: PropTypes.func,
    onClick: PropTypes.func,
    isLoggedIn: PropTypes.bool,
    added: PropTypes.func,
    removed: PropTypes.func,
    resultsPerRow: PropTypes.number,
    onMessageSent: PropTypes.func,
};
